import React, { useCallback, useEffect, useRef } from 'react';
import styles from './report-statement-quality-filter-bar.module.css';

/*
 * The Quality & Completeness filter bar: account type, years, and the
 * filtered-count readout, in one row that pins above the chart's month
 * axis. The PDF button deliberately lives outside, at the top of the page —
 * it is a page action, not a filter.
 *
 * The two dimensions get different controls because they scale differently:
 *
 *   Account type is BOUNDED. _ACCOUNT_TYPE_GROUPS in case_quality_report.py
 *   folds every account into at most eight groups, so a new account type never
 *   adds a ninth. But the labels are long ("Investment (Retirement)"), so it
 *   collapses into a disclosure with a summary rather than a row of chips.
 *
 *   Years are UNBOUNDED. A long marriage can span fifteen or more, and a chip
 *   per year wraps the bar into three or four rows. A from/to range holds a
 *   constant footprint no matter how many years the case covers.
 *
 * An earlier chip-per-value design failed on exactly that asymmetry.
 *
 * No inline arrow defaults in the destructure — the filter-stability standard
 * applies to anything feeding effect deps, and `onHeightChange` is exactly
 * that.
 */

const EMPTY = [];

/*
 * Years may have gaps — a case with nothing in 2022 yields
 * [2020, 2021, 2023, ...] — so a range selects the years that EXIST between
 * its endpoints rather than counting up from one to the other.
 */
export const yearsInRange = (allYears, from, to) => {
    if (!allYears || allYears.length === 0) return [];
    const lo = Number(from);
    const hi = Number(to);
    return allYears.filter((y) => Number(y) >= lo && Number(y) <= hi);
};

const summarize = (selected, all, noun) => {
    if (all.length === 0 || selected.length === 0) return `No ${noun}`;
    if (selected.length === all.length) return `All ${noun}`;
    return `${selected.length} of ${all.length} ${noun}`;
};

function AccountTypeFilter({ options, selected, onChange }) {
    const wrapRef = useRef(null);

    // <details> gives the disclosure toggle and keyboard handling for free;
    // this only adds the close-on-outside-click a dropdown is expected to have.
    useEffect(() => {
        const onDocumentClick = (event) => {
            const el = wrapRef.current;
            if (el && el.open && !el.contains(event.target)) el.open = false;
        };
        document.addEventListener('mousedown', onDocumentClick);
        return () => document.removeEventListener('mousedown', onDocumentClick);
    }, []);

    // Rebuilt from `options` rather than appended to, so the selection keeps
    // the backend's canonical group order however it was clicked together.
    const toggle = useCallback((value) => {
        const next = selected.includes(value)
            ? selected.filter((v) => v !== value)
            : options.filter((o) => selected.includes(o) || o === value);
        onChange(next);
    }, [selected, options, onChange]);

    const allSelected = options.length > 0 && selected.length === options.length;

    if (options.length === 0) return null;

    return (
        <details className={styles.dropdown} ref={wrapRef} data-testid="qr-type-filter">
            <summary className={styles.dropdownButton}>
                <span>{summarize(selected, options, 'types')}</span>
                <span className={styles.caret} aria-hidden="true">▾</span>
            </summary>

            <div className={styles.dropdownPanel} role="group" aria-label="Account type">
                <button
                    type="button"
                    className={styles.panelAction}
                    onClick={() => onChange(allSelected ? [] : [...options])}
                >
                    {allSelected ? 'Clear all' : 'Select all'}
                </button>

                {options.map((option) => (
                    <label key={option} className={styles.panelOption}>
                        <input
                            type="checkbox"
                            checked={selected.includes(option)}
                            onChange={() => toggle(option)}
                        />
                        {option}
                    </label>
                ))}
            </div>
        </details>
    );
}

function YearRangeFilter({ years, selected, onChange }) {
    // The range is derived from the selection rather than stored separately,
    // so there is one source of truth and the payload helpers keep taking a
    // plain list of years.
    const active = selected.length > 0 ? selected : years;
    const from = active[0];
    const to = active[active.length - 1];

    const setFrom = useCallback((value) => {
        const nextTo = Number(value) > Number(to) ? value : to;
        onChange(yearsInRange(years, value, nextTo));
    }, [years, to, onChange]);

    const setTo = useCallback((value) => {
        const nextFrom = Number(value) < Number(from) ? value : from;
        onChange(yearsInRange(years, nextFrom, value));
    }, [years, from, onChange]);

    if (years.length === 0) return null;

    return (
        <div className={styles.range} data-testid="qr-year-filter">
            <select
                className={styles.select}
                value={from}
                aria-label="From year"
                onChange={(event) => setFrom(event.target.value)}
            >
                {years.map((year) => <option key={year} value={year}>{year}</option>)}
            </select>
            <span className={styles.rangeTo}>to</span>
            <select
                className={styles.select}
                value={to}
                aria-label="To year"
                onChange={(event) => setTo(event.target.value)}
            >
                {years.map((year) => <option key={year} value={year}>{year}</option>)}
            </select>
        </div>
    );
}

export default function StatementQualityFilterBar({
    accountGroups: accountGroupsProp,
    selectedGroups: selectedGroupsProp,
    onGroupsChange,
    years: yearsProp,
    selectedYears: selectedYearsProp,
    onYearsChange,
    visibleAccountCount,
    totalAccountCount,
    onClear,
    onHeightChange,
    stickyTop,
}) {
    const accountGroups = accountGroupsProp || EMPTY;
    const selectedGroups = selectedGroupsProp || EMPTY;
    const years = yearsProp || EMPTY;
    const selectedYears = selectedYearsProp || EMPTY;

    const barRef = useRef(null);
    const lastHeightRef = useRef(null);

    /*
     * The month axis inside the chart pins too, so it has to sit below this
     * bar rather than under it. Its offset is this bar's height, measured
     * rather than hardcoded. Content-gated: reporting an unchanged height on
     * every render is the parent-notification loop the filter-stability
     * standard warns about.
     */
    useEffect(() => {
        const element = barRef.current;
        if (!element || !onHeightChange) return undefined;

        const report = () => {
            const height = element.getBoundingClientRect().height;
            if (lastHeightRef.current !== height) {
                lastHeightRef.current = height;
                onHeightChange(height);
            }
        };

        report();
        const observer = new ResizeObserver(report);
        observer.observe(element);
        return () => observer.disconnect();
    }, [onHeightChange]);

    const isFiltered = selectedGroups.length !== accountGroups.length
        || selectedYears.length !== years.length;

    return (
        <div
            className={styles.bar}
            ref={barRef}
            style={{ top: stickyTop }}
            data-testid="qr-filter-bar"
        >
            <div className={styles.control}>
                <span className={styles.label}>Account Type</span>
                <AccountTypeFilter
                    options={accountGroups}
                    selected={selectedGroups}
                    onChange={onGroupsChange}
                />
            </div>

            <div className={styles.control}>
                <span className={styles.label}>Years</span>
                <YearRangeFilter
                    years={years}
                    selected={selectedYears}
                    onChange={onYearsChange}
                />
            </div>

            <div className={styles.spacer} />

            {/* Always visible, so a narrowed view can never be mistaken for the
                whole report. */}
            <span
                className={`${styles.count} ${isFiltered ? styles.countFiltered : ''}`}
                data-testid="qr-filter-count"
            >
                Showing {visibleAccountCount} of {totalAccountCount} accounts
            </span>

            {isFiltered && (
                <button type="button" className={styles.clear} onClick={onClear}>
                    Clear filters
                </button>
            )}
        </div>
    );
}
